import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import type { FlexGaugeConfiguration, FlexGaugeState } from '../flex-monitor-status.models';

const highchartsMore: (highcharts: typeof Highcharts) => void = require('highcharts/highcharts-more');
highchartsMore(Highcharts);

const DEFAULT_CONFIGURATION: FlexGaugeConfiguration = {
    minimum: 0,
    maximum: 100,
    fairMinimum: 25,
    goodMinimum: 75,
};

@Component({
    selector: 'app-flex-status-gauge',
    templateUrl: './flex-status-gauge.component.html',
    styleUrls: ['./flex-status-gauge.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlexStatusGaugeComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('chartTarget') private chartTarget: ElementRef<HTMLDivElement>;

    @Input() public label = '';
    @Input() public value: number | null = null;
    @Input() public unit = '';
    @Input() public state: FlexGaugeState = 'unknown';
    @Input() public configuration: FlexGaugeConfiguration = DEFAULT_CONFIGURATION;
    @Input() public precision = 0;
    @Input() public unavailableText = '';
    @Input() public stateText = '';
    @Input() public isDarkTheme = false;

    private chart: Highcharts.Chart | null = null;
    private viewInitialized = false;

    public ngAfterViewInit(): void {
        this.viewInitialized = true;
        this.renderChart();
    }

    public ngOnChanges(): void {
        this.renderChart();
    }

    public ngOnDestroy(): void {
        this.chart?.destroy();
        this.chart = null;
    }

    public get validValue(): number | null {
        if (
            this.value === null ||
            !Number.isFinite(this.value) ||
            this.value < this.configuration.minimum ||
            this.value > this.configuration.maximum
        ) {
            return null;
        }
        return this.value;
    }

    public get displayValue(): string {
        return this.validValue === null
            ? this.unavailableText
            : `${this.validValue.toFixed(this.precision)} ${this.unit}`.trim();
    }

    public get ariaValueText(): string {
        return `${this.label}: ${this.displayValue}${this.stateText ? `, ${this.stateText}` : ''}`;
    }

    private renderChart(): void {
        if (!this.viewInitialized) return;

        this.chart?.destroy();
        const textColor = this.isDarkTheme ? 'rgba(255, 255, 255, 0.7)' : '#6a6a6a';
        const trackColor = this.isDarkTheme ? '#616161' : '#eeeeee';
        const pointerColor = this.isDarkTheme ? '#ffffff' : '#263238';
        const value = this.state === 'unknown' ? null : this.validValue;

        this.chart = Highcharts.chart(this.chartTarget.nativeElement, {
            chart: {
                type: 'gauge',
                height: 104,
                spacing: [0, 0, 0, 0],
                backgroundColor: 'transparent',
                animation: false,
                style: { fontFamily: "Roboto, 'Helvetica Neue', sans-serif" },
            },
            title: { text: null },
            credits: { enabled: false },
            exporting: { enabled: false },
            tooltip: { enabled: false },
            pane: {
                center: ['50%', '84%'],
                size: '125%',
                startAngle: -90,
                endAngle: 90,
                background: {
                    backgroundColor: trackColor,
                    borderWidth: 0,
                    innerRadius: '80%',
                    outerRadius: '100%',
                    shape: 'arc',
                },
            },
            yAxis: {
                min: this.configuration.minimum,
                max: this.configuration.maximum,
                lineWidth: 0,
                tickWidth: 0,
                minorTickInterval: null,
                tickPositions: [this.configuration.minimum, this.configuration.maximum],
                labels: {
                    y: 13,
                    style: {
                        color: textColor,
                        fontFamily: "Roboto, 'Helvetica Neue', sans-serif",
                        fontSize: '9px',
                        fontWeight: '400',
                    },
                },
                plotBands: [
                    {
                        from: this.configuration.minimum,
                        to: this.configuration.fairMinimum,
                        color: '#c62828',
                        innerRadius: '80%',
                        outerRadius: '100%',
                    },
                    {
                        from: this.configuration.fairMinimum,
                        to: this.configuration.goodMinimum,
                        color: '#ef6c00',
                        innerRadius: '80%',
                        outerRadius: '100%',
                    },
                    {
                        from: this.configuration.goodMinimum,
                        to: this.configuration.maximum,
                        color: '#2e7d32',
                        innerRadius: '80%',
                        outerRadius: '100%',
                    },
                ],
            },
            plotOptions: {
                gauge: {
                    animation: false,
                    dataLabels: { enabled: false },
                    dial: {
                        backgroundColor: pointerColor,
                        baseLength: '0%',
                        baseWidth: 3,
                        radius: '78%',
                        rearLength: '0%',
                        topWidth: 1,
                    },
                    pivot: {
                        backgroundColor: pointerColor,
                        radius: 3,
                    },
                },
            },
            series: [
                {
                    type: 'gauge',
                    name: this.label,
                    data: value === null ? [] : [value],
                },
            ],
        });
    }
}
